import axios, { AxiosResponse } from 'axios'
import Config from '../../config'
import { ISubmissionRecord, ISubmissionRecordV2, SubmissionCountry } from './types'
const { restful } = Config

export interface UserLoggedInType {
  createdAt: string
  deviceType: 'WEBSITE'
  expireAt: string
  projectName: 'vehicle-submission'
  tokenType: 'accessToken'
  updatedAt: string
  userId: number
  userToken: string
  __v: number
  _id: string
}

export const getOwnRecord = async (
  userLoggedIn: UserLoggedInType,
): Promise<ISubmissionRecordV2[]> => {
  // console.log(user)
  return new Promise((resolve, reject) => {
    axios
      .get(`${restful.URL}/submission`, {
        headers: {
          Authorization: userLoggedIn.userToken,
        },
      })
      .then((response: AxiosResponse<ISubmissionRecordV2[]>) => resolve(response.data))
      .catch((error) => reject(error))
  })
}

export const getRecordById = async (
  userLoggedIn: UserLoggedInType,
  id: string,
): Promise<ISubmissionRecordV2> => {
  return new Promise((resolve, reject) => {
    axios
      .get(`${restful.URL}/submission/v2/${id}`, {
        headers: {
          Authorization: userLoggedIn.userToken,
        },
      })
      .then((response: AxiosResponse<ISubmissionRecordV2>) => {
        return response.data
      })
      .then((parsed) => resolve(parsed))
      .catch((error) => reject(error))
  })
}

export const deleteRecord = async (userLoggedIn: UserLoggedInType, id: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    axios
      .delete(`${restful.URL}/submission/${id}`, {
        headers: {
          Authorization: userLoggedIn.userToken,
        },
      })
      .then((response) => resolve(response.data))
      .catch((error) => reject(error))
  })
}

export const submitRecord = async (
  userLoggedIn: UserLoggedInType,
  formData: ISubmissionRecord,
): Promise<ISubmissionRecord> => {
  return new Promise((resolve, reject) => {
    axios
      .post(
        `${restful.URL}/submission`,
        { ...formData, ownerId: userLoggedIn.userId },
        {
          headers: {
            Authorization: userLoggedIn.userToken,
          },
        },
      )
      .then((response) => resolve(response.data))
      .catch((error) => reject(error))
  })
}

export const getPlace = (userLoggedIn: UserLoggedInType, lat: number, lng: number) => {
  return new Promise((resolve, reject) => {
    axios
      .get(`${restful.URL}/submission/geocoding`, {
        headers: { Authorization: userLoggedIn.userToken },
        params: { lat, lng },
      })
      .then((response) => {
        const parkingGeo: string = response.data.data.results[0]?.formatted_address
        resolve(parkingGeo)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export const updateRecord = (
  userLoggedIn: UserLoggedInType,
  recordId: string,
  data: ISubmissionRecord,
): Promise<{
  n: number
  nModified: number
  ok: number
}> => {
  return new Promise((resolve, reject) => {
    axios
      .post(
        `${restful.URL}/submission`,
        { ...data, _id: recordId },
        {
          headers: {
            Authorization: userLoggedIn.userToken,
          },
        },
      )
      .then((response) => resolve(response.data))
      .catch((error) => reject(error))
  })
}

interface CheckDuplicateLicensePlateReturnType {
  vehicleId: number
  email: string
  licensePlate: string
  licenseProvince: string
}

export async function checkDuplicateLicensePlate(
  userLoggedIn: UserLoggedInType,
  plateNumber: string,
  province: string,
) {
  const results = (await axios.post(
    `${restful.URL}/submission/v2/check/duplicate/licenseplate`,
    {
      plateNumber,
      province,
    },
    {
      headers: { Authorization: userLoggedIn.userToken },
    },
  )) as AxiosResponse<CheckDuplicateLicensePlateReturnType[]>
  return results.data
}

export async function getProvinces(userLoggedIn: UserLoggedInType) {
  const results: AxiosResponse<string[]> = await axios.get(
    `${restful.URL}/submission/v2/province/all`,
    {
      headers: { Authorization: userLoggedIn.userToken },
    },
  )
  return results.data
}

export async function submitRecordV2(
  userLoggedIn: UserLoggedInType,
  formData: ISubmissionRecordV2,
) {
  const created: AxiosResponse<ISubmissionRecordV2> = await axios.post(
    `${restful.URL}/submission/v2`,
    formData,
    {
      headers: { Authorization: userLoggedIn.userToken },
    },
  )
  if (!created?.data?._id) {
    throw 'Submit failed'
  }
  return created.data
}

export async function updateRecordV2(
  userLoggedIn: UserLoggedInType,
  recordId: string,
  formData: ISubmissionRecordV2,
) {
  const updated: AxiosResponse<ISubmissionRecordV2> = await axios.put(
    `${restful.URL}/submission/v2/${recordId}`,
    { ...formData },
    {
      headers: { Authorization: userLoggedIn.userToken },
    },
  )
  return updated.data
}

export async function getIdCardImage(userLoggedIn: UserLoggedInType) {
  const response = await axios.get<{ data: string }>(`${restful.URL}/submission/v2/idcardimage`, {
    headers: { Authorization: userLoggedIn.userToken },
  })
  return response.data
}

export interface StandardVehiclePrice {
  packageType: 'PRO'
  haupSharingPercentage: number
  partnerSharingPercentage: number
  dailyRateNormal: number
  dailyRateAdjustMin: number
  dailyRateAdjustMax: number
  hourlyRate: number
  weeklyRate: number
  weeklyRateAdjustMin: number
  weeklyRateAdjustMax: number
  monthlyRate: number
  monthlyRateAdjustMin: number
  monthlyRateAdjustMax: number
  yearlyRate: number
  yearlyRateAdjustMin: number
  yearlyRateAdjustMax: number
}

interface GetStandardPriceResponse {
  result: boolean
  data: StandardVehiclePrice
}

export async function getStandardVehiclePrice(
  userLoggedIn: UserLoggedInType,
  brand: string,
  model: string,
  subModel?: string,
  vehicleYear?: number | string,
): Promise<StandardVehiclePrice> {
  const response = await axios.get<GetStandardPriceResponse>(
    `${restful.URL}/submission/v2/standard-price`,
    {
      params: {
        brand,
        model,
        sub_model: subModel || '',
        year: vehicleYear || '',
      },
      headers: { Authorization: userLoggedIn.userToken },
    },
  )
  return response.data.data
}

// Public endpoint (no auth required) - safe to call from pre-login pages.
export async function getStandardVehiclePricePublic(
  brand: string,
  model: string,
  subModel?: string,
  vehicleYear?: number | string,
): Promise<StandardVehiclePrice> {
  const response = await axios.get<GetStandardPriceResponse>(
    `${restful.URL}/submission/v2/standard-price`,
    {
      params: {
        brand,
        model,
        sub_model: subModel || '',
        year: vehicleYear || '',
      },
    },
  )
  return response.data.data
}

export interface SubmissionExchangeRate {
  country: SubmissionCountry
  base_currency: string
  currency: string
  rate: number
  date: string
}

export async function getSubmissionExchangeRate(
  country: SubmissionCountry,
): Promise<SubmissionExchangeRate> {
  const response = await axios.get<{ result: boolean; data: SubmissionExchangeRate }>(
    `${restful.URL}/submission/v2/exchange-rate`,
    { params: { country } },
  )
  return response.data.data
}

export interface VehiclePackageV2 {
  vehicle_model_id: number
  vehicle_key: string
  brand: string
  sub_model: string
  vehiclesize: string
  vehicletype: string
  seat: string
  priceid: number
  pricecode: string
  daily_rate: number | null
  weekly_rate: number | null
  monthly_rate: number | null
  yearly_rate: number | null
}

interface GetVehiclePackageV2Response {
  result: boolean
  data: VehiclePackageV2
}

// Public endpoint (no auth required) - safe to call from pre-login pages.
export async function getVehiclePackageV2(brand: string, model: string): Promise<VehiclePackageV2> {
  const response = await axios.get<GetVehiclePackageV2Response>(
    `${restful.URL}/submission/v2/vehiclepackage-v2`,
    { params: { brand, model } },
  )
  return response.data.data
}

interface GetTermAndConditionsResponse {
  result: boolean
  data: termAndConditions[]
}

export interface termAndConditions {
  consentId: number
  consentSubject: string
  consentDescription: string
  consentTextTh: string
  consentTextEn: string
}

export async function getTermandCondition(
  userLoggedIn: UserLoggedInType,
): Promise<termAndConditions[]> {
  const response = await axios.get<GetTermAndConditionsResponse>(
    `${restful.URL}/submission/v2/termandconditions`,
    {
      headers: { Authorization: userLoggedIn.userToken },
    },
  )

  return response.data.data
}

interface GetVehiclePackageResponse {
  result: boolean
  data: vehiclePackage[]
}

export interface vehiclePackage {
  packgaeType: string
  brand: string
  model: string
  haupSharingPercentage: number
  partnerSharingPercentage: number
  dailyRateNormal: number
  dailyRateAdjustMin: number
  dailyRateAdjustMax: number
}

export async function getVehiclePackage(
  userLoggedIn: UserLoggedInType,
  brand: string | undefined,
  model: string | undefined,
): Promise<vehiclePackage[]> {
  const response = await axios.get<GetVehiclePackageResponse>(
    `${restful.URL}/submission/v2/vehiclepackage?brand=${brand}&model=${model}`,
    {
      headers: { Authorization: userLoggedIn.userToken },
    },
  )

  return response.data.data
}

export async function transferSubmissionToVehicle(
  userLoggedIn: UserLoggedInType,
  submissionId: string,
) {
  const response = await axios.post(
    `${restful.URL}/submission/v2/${submissionId}/finish`,
    {},
    {
      headers: { Authorization: userLoggedIn.userToken },
    },
  )
  return response.data
}

export async function transferSubmissionToVehicleV2(
  userLoggedIn: UserLoggedInType,
  submissionId: string,
  priceid: number,
  pricecode: string,
  vehicleId?: number,
  depositPrice?: number,
  isRentalBusiness?: boolean,
  subscriptionPackages?: string,
  lang?: 'th' | 'en',
) {
  const response = await axios.post(
    `${restful.URL}/submission/v2/${submissionId}/finish-v2`,
    {
      priceid,
      pricecode,
      ...(vehicleId && { vehicleId }),
      ...(depositPrice !== undefined && { deposit_price: depositPrice }),
      ...(isRentalBusiness !== undefined && { is_rental_business: isRentalBusiness }),
      subscription_packages: subscriptionPackages || '',
      ...(lang && { lang }),
    },
    {
      headers: { Authorization: userLoggedIn.userToken },
    },
  )

  // Pre-generate the draft rental contract, but ONLY on the first-time transfer that
  // actually creates the vehicle (no vehicleId passed in). When the owner comes back
  // after submitting - re-syncing photos/documents, Save & Exit, after_submitted flow -
  // a vehicleId is always supplied, and we must not touch/override the existing draft.
  // Best-effort: this must never break or delay-fail the submission flow.
  const finishedVehicleId = response.data?.data?.vehicleid as number | undefined
  if (!vehicleId && finishedVehicleId) {
    try {
      await createVehicleContractDraft(userLoggedIn, finishedVehicleId, lang)
    } catch (contractError) {
      console.error('Failed to create draft vehicle contract', contractError)
    }
  }

  return response.data
}

export interface VehicleContractDraft {
  required: boolean
  contractId: number | null
  contractNumber: string | null
  termsVersion: string
  status: string | null
  signStatus: 'SIGNED' | 'UNSIGNED'
  signedAt: string | null
  language: 'TH' | 'EN' | null
  draftDownloadUrl: string | null
  draftDownloadUrlTh: string | null
  draftDownloadUrlEn: string | null
  finalDownloadUrl: string | null
  finalDownloadUrlTh: string | null
  finalDownloadUrlEn: string | null
  ownerAddressRequired: boolean
  ownerFirstNameRequired: boolean
  ownerLastNameRequired: boolean
}

/**
 * Creates (or returns the existing) draft rental contract for a vehicle that was
 * just transferred from a submission via finish-v2. Best-effort: callers should
 * not block the submission flow on this.
 */
export async function createVehicleContractDraft(
  userLoggedIn: UserLoggedInType,
  vehicleId: number,
  lang?: 'th' | 'en' | 'TH' | 'EN',
): Promise<VehicleContractDraft> {
  const response = await axios.post<{ result: boolean; data: VehicleContractDraft }>(
    `${restful.URL}/mobile/host/vehicle/${vehicleId}/contract/draft`,
    { language: String(lang || 'TH').toUpperCase() },
    {
      headers: { Authorization: userLoggedIn.userToken },
    },
  )
  return response.data.data
}

export interface VehiclePreviewData {
  vehicleid: number
  vehiclebrand: string
  vehiclemodel: string
  vehicleyear: string
  vehicleseat: string
  station: {
    name: string
    address: string
    stationid: number
  }
  durationrate_label: string
  vehicletypedetail: {
    value: string
    label: {
      en: string
      th: string
    }
    imageUrl: string
  }
  detail: Array<{ key: string; value: string }>
  picture: Array<{ url: string }>
}

export const getVehiclePreview = async (
  userLoggedIn: UserLoggedInType,
  vehicleId: number,
): Promise<VehiclePreviewData | null> => {
  try {
    const response = await axios.get<{ result: boolean; data?: VehiclePreviewData }>(
      `${restful.URL}/submission/v2/vehicle-preview/${vehicleId}`,
      { headers: { Authorization: userLoggedIn.userToken } },
    )
    return response.data?.data || null
  } catch (error) {
    return null
  }
}

export const getGeneratedVehiclePicture = async (
  userLoggedIn: UserLoggedInType,
  vehicleId: number,
): Promise<string | null> => {
  try {
    const response = await axios.get<{ result: boolean; data?: { url: string | null } }>(
      `${restful.URL}/submission/v2/generated-picture/${vehicleId}`,
      { headers: { Authorization: userLoggedIn.userToken } },
    )
    return response.data?.data?.url || null
  } catch (error) {
    return null
  }
}

export interface GeneratedPictureQuery {
  id: string
  vehicleId?: number
  brand?: string
  model?: string
  subModel?: string
}

export const getGeneratedVehiclePictures = async (
  userLoggedIn: UserLoggedInType,
  items: GeneratedPictureQuery[],
): Promise<Record<string, string | null>> => {
  if (!items.length) {
    return {}
  }
  try {
    const response = await axios.post<{ result: boolean; data?: Array<{ url: string | null }> }>(
      `${restful.URL}/submission/v2/generated-pictures`,
      {
        items: items.map(({ vehicleId, brand, model, subModel }) => ({
          vehicleId,
          brand,
          model,
          subModel,
        })),
      },
      { headers: { Authorization: userLoggedIn.userToken } },
    )
    const data = response.data?.data || []
    return items.reduce<Record<string, string | null>>((acc, item, index) => {
      acc[item.id] = data[index]?.url || null
      return acc
    }, {})
  } catch (error) {
    return {}
  }
}
